import React from "react";
import { useTranslation } from "react-i18next";
import { UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";

const AssignmentCreateDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  employees,
  schedules,
  cyclePatterns,
  handleCreateAssignment,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("planning.assignmentsPanel.createTitle")}</DialogTitle>
          <DialogDescription>
            {t("planning.assignmentsPanel.createDescription")}
          </DialogDescription>
        </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{t("planning.assignmentsPanel.mode")}</Label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="assignment_mode"
                value="single"
                checked={formData.assignment_mode === "single"}
                onChange={(e) => setFormData({ ...formData, assignment_mode: e.target.value })}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">{t("planning.assignmentsPanel.singleEmployee")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="assignment_mode"
                value="bulk"
                checked={formData.assignment_mode === "bulk"}
                onChange={(e) => setFormData({ ...formData, assignment_mode: e.target.value })}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">{t("planning.assignmentsPanel.bulkEmployees")}</span>
            </label>
          </div>
        </div>

        {formData.assignment_mode === "single" ? (
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.employee")}</Label>
            <Select
              value={formData.employee_id}
              onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
            >
              <SelectTrigger><SelectValue placeholder={t("planning.assignmentsPanel.selectEmployee")} /></SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id.toString()}>
                    {emp.last_name} {emp.first_name} - {emp.badge_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.employeesSelected", { count: formData.employee_ids.length })}</Label>
            <div className="border rounded-md max-h-48 overflow-y-auto p-3 bg-muted/50">
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-card p-1 rounded">
                  <input
                    type="checkbox"
                    checked={formData.employee_ids.length === employees.length}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employee_ids: e.target.checked ? employees.map((emp) => emp.id.toString()) : [],
                      })
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium">{t("planning.assignmentsPanel.selectAll")}</span>
                </label>
                <hr className="my-2" />
                {employees.map((emp) => (
                  <label key={emp.id} className="flex items-center gap-2 cursor-pointer hover:bg-card p-1 rounded">
                    <input
                      type="checkbox"
                      checked={formData.employee_ids.includes(emp.id.toString())}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          employee_ids: e.target.checked
                            ? [...formData.employee_ids, emp.id.toString()]
                            : formData.employee_ids.filter((id) => id !== emp.id.toString()),
                        })
                      }
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">{emp.last_name} {emp.first_name} - {emp.badge_number}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>{t("planning.assignmentsPanel.assignmentType")}</Label>
          <Select
            value={formData.assignment_type}
            onValueChange={(value) => setFormData({ ...formData, assignment_type: value })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="schedule">{t("planning.assignmentsPanel.fixedSchedule")}</SelectItem>
              <SelectItem value="cycle">{t("planning.assignmentsPanel.rotatingCycle")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.assignment_type === "schedule" ? (
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.schedule")}</Label>
            <Select
              value={formData.schedule_id}
              onValueChange={(value) => setFormData({ ...formData, schedule_id: value })}
            >
              <SelectTrigger><SelectValue placeholder={t("planning.assignmentsPanel.selectSchedule")} /></SelectTrigger>
              <SelectContent>
                {schedules.map((s) => (
                  <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label>{t("planning.assignmentsPanel.cycle")}</Label>
              <Select
                value={formData.cycle_pattern_id}
                onValueChange={(value) => setFormData({ ...formData, cycle_pattern_id: value })}
              >
                <SelectTrigger><SelectValue placeholder={t("planning.assignmentsPanel.selectCycle")} /></SelectTrigger>
                <SelectContent>
                  {cyclePatterns.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name} ({t("planning.cycles.weekCount", { count: c.cycle_duration_weeks })})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("planning.assignmentsPanel.cycleStartWeek")}</Label>
              <Input
                type="number"
                min="1"
                value={formData.cycle_start_week}
                onChange={(e) => setFormData({ ...formData, cycle_start_week: parseInt(e.target.value) || 1 })}
              />
              <p className="text-xs text-muted-foreground">{t("planning.assignmentsPanel.cycleStartHelp")}</p>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.startDate")}</Label>
            <Input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.endDate")}</Label>
            <Input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              min={formData.start_date}
            />
            <p className="text-xs text-muted-foreground">{t("planning.assignmentsPanel.permanentHelp")}</p>
          </div>
        </div>

        {formData.assignment_mode === "bulk" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-3 border rounded-lg bg-amber-950/20 border-amber-900/40">
              <input
                type="checkbox"
                id="replace-existing"
                checked={formData.replace_existing}
                onChange={(e) => setFormData({ ...formData, replace_existing: e.target.checked })}
                className="w-4 h-4 text-blue-600"
              />
              <Label htmlFor="replace-existing" className="cursor-pointer text-sm">
                {t("planning.assignmentsPanel.replaceExisting")}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("planning.assignmentsPanel.replaceExistingHelp")}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>{t("planning.assignmentsPanel.reason")}</Label>
          <Textarea
            placeholder={t("planning.assignmentsPanel.reasonPlaceholder")}
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t("planning.scheduleManager.actions.cancel")}</Button>
        <Button onClick={handleCreateAssignment}>
          <UserPlus className="h-4 w-4 mr-2" />
          {t("planning.assignmentsPanel.createAction")}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default AssignmentCreateDialog;
